// pages/UserManagementPage.js

import React, { useState } from "react";
import useManageUsers from "../hooks/useManageUsers";
import Swal from "sweetalert2"; // npm install sweetalert2
import "../App.css";

// مكون النافذة المنبثقة لعرض تفاصيل المستخدم
const UserDetailsModal = ({ user, onClose }) => {
  if (!user) return null;

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const formatDate = (date) => {
    if (!date) return "غير محدد";
    return new Date(date).toLocaleDateString("ar-SA");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={stopPropagation}>
        <div className="modal-header">
          <h2>تفاصيل المستخدم</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="user-detail">
            <strong>الاسم:</strong>
            <span>{user.name || "غير محدد"}</span>
          </div>
          <div className="user-detail">
            <strong>البريد الإلكتروني:</strong>
            <span>{user.email || "غير محدد"}</span>
          </div>
          <div className="user-detail">
            <strong>الدور:</strong>
            <span>{user.role || "غير محدد"}</span>
          </div>
          <div className="user-detail">
            <strong>الحالة:</strong>
            <span
              className={user.isActive ? "status-active" : "status-inactive"}
            >
              {user.isActive ? "نشط" : "معطل"}
            </span>
          </div>

          {user.role === "member" && (
            <>
              <div className="user-detail">
                <strong>النقاط:</strong>
                <span>{user.points || 0}</span>
              </div>
              <div className="user-detail">
                <strong>كود الإحالة:</strong>
                <span>{user.referralCode || "غير محدد"}</span>
              </div>
              <div className="user-detail">
                <strong>رقم الهاتف:</strong>
                <span>{user.phoneNumber || "غير محدد"}</span>
              </div>
              <div className="user-detail">
                <strong>المدينة:</strong>
                <span>{user.city || "غير محدد"}</span>
              </div>
              <div className="user-detail">
                <strong>الحي:</strong>
                <span>{user.district || "غير محدد"}</span>
              </div>
              <div className="user-detail">
                <strong>تاريخ الميلاد:</strong>
                <span>{formatDate(user.birthDate)}</span>
              </div>
              <div className="user-detail">
                <strong>تاريخ الانضمام:</strong>
                <span>{formatDate(user.joinDate)}</span>
              </div>
              <div className="user-detail">
                <strong>مستوى العضوية:</strong>
                <span className={`membership-${user.membershipLevel}`}>
                  {user.membershipLevel === "bronze" && "برونزي"}
                  {user.membershipLevel === "silver" && "فضي"}
                  {user.membershipLevel === "gold" && "ذهبي"}
                  {user.membershipLevel === "platinum" && "بلاتيني"}
                </span>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="primary-button" onClick={onClose}>
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

function UserManagementPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    points: 0,
    referralCode: "",
    phoneNumber: "",
    city: "",
    district: "",
    birthDate: "",
    joinDate: "",
    membershipLevel: "bronze",
  });
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [activationLink, setActivationLink] = useState("");
  const [activationMsg, setActivationMsg] = useState("");
  const [copiedUserId, setCopiedUserId] = useState(null);

  const handleCopyPassword = (userId, password) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopiedUserId(userId);
      setTimeout(() => setCopiedUserId(null), 1500);
    });
  };

  const {
    users,
    roles,
    selectedUser,
    setSelectedUser,
    addUser,
    updateUser,
    deleteUserAccount,
    toggleUserAccount,
    fetchUsers,
    cities,
    districts,
    updateDistricts,
    loading,
    error,
    activateUser,
  } = useManageUsers(recordsPerPage, searchTerm);

  const validateForm = () => {
    const errors = {};
    if (!userForm.name.trim()) errors.name = "الاسم مطلوب";
    if (!userForm.email.trim()) {
      errors.email = "البريد الإلكتروني مطلوب";
    } else if (!/\S+@\S+\.\S+/.test(userForm.email)) {
      errors.email = "البريد الإلكتروني غير صالح";
    }
    if (!selectedUser && !userForm.password) {
      errors.password = "كلمة المرور مطلوبة";
    }
    if (!userForm.role) errors.role = "الدور مطلوب";

    if (userForm.role === "member") {
      if (!userForm.city) errors.city = "المدينة مطلوبة";
      if (!userForm.district) errors.district = "الحي مطلوب";
      if (!userForm.phoneNumber) errors.phoneNumber = "رقم الهاتف مطلوب";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));

    // مسح رسالة الخطأ عند الكتابة
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "role") {
      handleRoleChange(value);
    } else if (name === "city") {
      updateDistricts(value);
      setUserForm((prev) => ({ ...prev, district: "" }));
    }
  };

  const handleRoleChange = (role) => {
    if (role === "member") {
      setAdditionalFields([
        "points",
        "referralCode",
        "phoneNumber",
        "city",
        "district",
        "birthDate",
        "membershipLevel",
      ]);
    } else {
      setAdditionalFields([]);
      // إعادة تعيين الحقول الإضافية
      setUserForm((prev) => ({
        ...prev,
        points: 0,
        referralCode: "",
        phoneNumber: "",
        city: "",
        district: "",
        birthDate: "",
        membershipLevel: "bronze",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, userForm);
        Swal.fire({ icon: "success", title: "تم التحديث", text: "تم تحديث المستخدم بنجاح", timer: 2000, showConfirmButton: false });
      } else {
        await addUser(userForm);
        Swal.fire({ icon: "success", title: "تمت الإضافة", text: "تم إضافة المستخدم بنجاح", timer: 2000, showConfirmButton: false });
      }
      clearForm();
    } catch (error) {
      Swal.fire({ icon: "error", title: "خطأ", text: error.message });
    }
  };

  const clearForm = () => {
    setUserForm({
      name: "",
      email: "",
      password: "",
      role: "",
      points: 0,
      referralCode: "",
      phoneNumber: "",
      city: "",
      district: "",
      birthDate: "",
      joinDate: "",
      membershipLevel: "bronze",
    });
    setSelectedUser(null);
    setAdditionalFields([]);
    setFormErrors({});
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      ...user,
      password: "", // لا نعرض كلمة المرور عند التعديل
    });
    handleRoleChange(user.role);
  };

  const handleDeleteUser = async (userId) => {
    const result = await Swal.fire({
      title: "حذف المستخدم",
      text: "هل أنت متأكد من حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "نعم، احذف",
      cancelButtonText: "إلغاء",
    });
    if (!result.isConfirmed) return;

    try {
      await deleteUserAccount(userId);
      Swal.fire({ icon: "success", title: "تم الحذف", text: "تم حذف المستخدم بنجاح", timer: 2000, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "خطأ", text: error.message });
    }
  };

  const handleToggleUser = async (userId, isActive) => {
    const action = isActive ? "تعطيل" : "تفعيل";
    const result = await Swal.fire({
      title: `${action} الحساب`,
      text: `هل أنت متأكد من ${action} حساب هذا المستخدم؟`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `نعم، ${action}`,
      cancelButtonText: "إلغاء",
    });
    if (!result.isConfirmed) return;

    try {
      await toggleUserAccount(userId, isActive);
      Swal.fire({ icon: "success", title: "تم بنجاح", text: `تم ${action} الحساب بنجاح`, timer: 2000, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "خطأ", text: error.message });
    }
  };

  const handleActivateUser = async (userId) => {
    const confirm = await Swal.fire({
      title: "تفعيل المستخدم",
      text: "تأكيد تفعيل هذا المستخدم؟",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "نعم، فعّل",
      cancelButtonText: "إلغاء",
    });
    if (!confirm.isConfirmed) return;

    try {
      const result = await activateUser(userId);
      setActivationLink(result.redirectUrl || "");
      setActivationMsg(
        "تم تفعيل المستخدم! هذا هو رابط التفعيل النهائي (يمكنك نسخه وإرساله للمستخدم):"
      );
      fetchUsers();
    } catch (err) {
      setActivationLink("");
      setActivationMsg("حدث خطأ أثناء تفعيل المستخدم");
      Swal.fire({ icon: "error", title: "خطأ", text: "حدث خطأ أثناء تفعيل المستخدم" });
    }
  };

  const handleShowUserDetails = (user) => {
    setUserDetails(user);
    setShowUserDetailsModal(true);
  };

  // === التعديل الرئيسي هنا ===
  const handleActivateOrRegenerate = async (user) => {
    try {
      const result = await activateUser(user.id);
      setActivationLink(result.redirectUrl || "");
      setActivationMsg(
        user.isActive
          ? "تم توليد رابط تفعيل جديد (JWT) بنجاح! يمكنك نسخه أو إرساله للمستخدم:"
          : "تم تفعيل المستخدم! هذا هو رابط التفعيل النهائي (يمكنك نسخه وإرساله للمستخدم):"
      );
      fetchUsers(); // تحديث الجدول بعد التفعيل

      // Show SweetAlert with the link and copy button
      await Swal.fire({
        title: user.isActive ? "رابط تفعيل جديد" : "تم تفعيل المستخدم!",
        html: `
          <div style="direction: ltr; word-break: break-all;">
            <input id="swal-input-link" value="${result.redirectUrl}" readonly style="width:99%; padding:5px;" />
            <button id="swal-copy-btn" style="margin-top:8px;padding:6px 16px;background:#4db06b;color:#fff;border:none;cursor:pointer;border-radius:5px;">نسخ الرابط</button>
          </div>
        `,
        showCloseButton: true,
        showConfirmButton: false,
        didOpen: () => {
          const input = document.getElementById("swal-input-link");
          input?.addEventListener("focus", () => input.select());
          document.getElementById("swal-copy-btn").onclick = () => {
            navigator.clipboard.writeText(result.redirectUrl);
            Swal.fire({
              icon: "success",
              title: "تم نسخ الرابط!",
              timer: 1200,
              showConfirmButton: false,
              toast: true,
              position: "top",
              customClass: { popup: "swal2-toast-ar" },
            });
          };
        },
      });
    } catch (err) {
      setActivationLink("");
      setActivationMsg("حدث خطأ أثناء تفعيل أو توليد الرابط");
      Swal.fire({
        icon: "error",
        title: "خطأ!",
        text: "حدث خطأ أثناء تفعيل أو توليد الرابط",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className="container" dir="rtl">
      {/* إضافة CSS محلي لإلغاء أي قيود عرض من App.css */}
      <style>
        {`
          /* حل ذكي: إلغاء table-layout: fixed للجدول ده بس */
          .users-table {
            table-layout: auto !important;
            width: max-content !important;
            min-width: 100% !important;
          }

          /* جعل الجدول قابل للتمرير الأفقي */
          .table-container {
            overflow-x: auto !important;
            overflow-y: visible !important;
          }

          /* تحديد عرض ثابت للأعمدة الأخرى لضمان التنسيق */
          .users-table th:nth-child(1),
          .users-table td:nth-child(1) {
            width: 150px !important;
            min-width: 150px !important;
            max-width: 150px !important;
          }

          .users-table th:nth-child(2),
          .users-table td:nth-child(2) {
            width: 200px !important;
            min-width: 200px !important;
            max-width: 200px !important;
          }

          .users-table th:nth-child(3),
          .users-table td:nth-child(3) {
            width: 100px !important;
            min-width: 100px !important;
            max-width: 100px !important;
          }

          .users-table th:nth-child(4),
          .users-table td:nth-child(4) {
            width: 120px !important;
            min-width: 120px !important;
            max-width: 120px !important;
          }

          .users-table th:nth-child(5),
          .users-table td:nth-child(5) {
            width: 80px !important;
            min-width: 80px !important;
            max-width: 80px !important;
          }

          /* عمود كلمة المرور */
          .users-table th:nth-child(6),
          .users-table td:nth-child(6) {
            width: 160px !important;
            min-width: 160px !important;
            max-width: 160px !important;
          }

          .password-cell {
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
          }

          .password-text {
            font-family: monospace;
            font-size: 13px;
            color: #555;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 110px;
            direction: ltr;
            text-align: left;
          }

          .copy-btn {
            background: none !important;
            border: 1px solid #ddd !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            padding: 3px 6px !important;
            font-size: 14px !important;
            color: #666 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            transition: all 0.2s ease !important;
            min-width: auto !important;
            line-height: 1 !important;
          }

          .copy-btn:hover {
            background-color: #f0f0f0 !important;
            border-color: #bbb !important;
            color: #333 !important;
          }

          .copy-btn.copied {
            background-color: #e8f5e9 !important;
            border-color: #4CAF50 !important;
            color: #4CAF50 !important;
          }

          /* عمود الإجراءات ياخد باقي المساحة ويتوسع حسب الحاجة */
          .users-table th:nth-child(7),
          .users-table .actions-cell {
            width: auto !important;
            min-width: 400px !important;
            white-space: nowrap !important;
            padding: 12px !important;
          }
          
          /* تحسين تصميم الأزرار بقوة */
          .users-table .actions-cell button {
            min-width: 40px !important;
            padding: 8px !important;
            margin: 0 3px !important;
            font-size: 14px !important;
            font-weight: 500 !important;
            border-radius: 4px !important;
            border: none !important;
            cursor: pointer !important;
            white-space: nowrap !important;
            display: inline-block !important;
            vertical-align: middle !important;
            line-height: 1.2 !important;
          }
          
          /* الأزرار النصية (توليد رابط فقط) */
          .users-table .actions-cell .activate-button {
            min-width: 120px !important;
            padding: 8px 12px !important;
            font-size: 13px !important;
          }
          
          /* ألوان الأزرار */
          .users-table .actions-cell .edit-button {
            background-color: #4CAF50 !important;
            color: white !important;
          }
          
          .users-table .actions-cell .delete-button {
            background-color: #f44336 !important;
            color: white !important;
          }
          
          .users-table .actions-cell .details-button {
            background-color: #2196F3 !important;
            color: white !important;
          }
          
          .users-table .actions-cell .activate-button {
            background-color: #FF9800 !important;
            color: white !important;
            min-width: 130px !important;
          }
          
          .users-table .actions-cell .deactivate-button {
            background-color: #9E9E9E !important;
            color: white !important;
          }
          
          /* تأثيرات hover */
          .users-table .actions-cell .edit-button:hover {
            background-color: #45a049 !important;
          }
          
          .users-table .actions-cell .delete-button:hover {
            background-color: #da190b !important;
          }
          
          .users-table .actions-cell .details-button:hover {
            background-color: #1976D2 !important;
          }
          
          .users-table .actions-cell .activate-button:hover {
            background-color: #F57C00 !important;
          }
          
          .users-table .actions-cell .deactivate-button:hover {
            background-color: #757575 !important;
          }
          
          /* الأزرار المعطلة */
          .users-table .actions-cell button:disabled {
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }
          
          /* تحسين الجدول للشاشات الصغيرة */
          @media (max-width: 768px) {
            .users-table .actions-cell {
              min-width: 300px !important;
            }
            
            .users-table .actions-cell button {
              min-width: 70px !important;
              padding: 6px 8px !important;
              font-size: 12px !important;
              margin: 0 2px !important;
            }
            
            .users-table .actions-cell .activate-button {
              min-width: 110px !important;
            }
          }
        `}
      </style>

      <h1 className="page-title">إدارة المستخدمين</h1>

      {/* يظهر الرابط كإشعار فوق الجدول (اختياري) */}
      {activationLink && (
        <div className="activation-link-container">
          <div className="activation-msg">{activationMsg}</div>
          <div className="activation-link-row">
            <input
              type="text"
              className="activation-link-input"
              value={activationLink}
              readOnly
              style={{ width: "75%" }}
              onFocus={(e) => e.target.select()}
            />
            <button
              className="copy-link-btn"
              onClick={() => {
                navigator.clipboard.writeText(activationLink);
                Swal.fire({
                  icon: "success",
                  title: "تم نسخ الرابط!",
                  timer: 1200,
                  showConfirmButton: false,
                  toast: true,
                  position: "top",
                  customClass: { popup: "swal2-toast-ar" },
                });
              }}
              style={{ marginRight: "10px" }}
            >
              نسخ الرابط
            </button>
            <button
              className="close-link-btn"
              onClick={() => {
                setActivationLink("");
                setActivationMsg("");
              }}
            >
              إخفاء
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {/* نموذج إضافة/تعديل المستخدم */}
      <div className="user-form card">
        <h2>{selectedUser ? "تعديل مستخدم" : "إضافة مستخدم جديد"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>الاسم:</label>
            <input
              type="text"
              name="name"
              value={userForm.name}
              onChange={handleInputChange}
              className={`form-control ${formErrors.name ? "error" : ""}`}
            />
            {formErrors.name && (
              <span className="error-message">{formErrors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label>البريد الإلكتروني:</label>
            <input
              type="email"
              name="email"
              value={userForm.email}
              onChange={handleInputChange}
              disabled={!!selectedUser}
              className={`form-control ${formErrors.email ? "error" : ""}`}
            />
            {formErrors.email && (
              <span className="error-message">{formErrors.email}</span>
            )}
          </div>

          {!selectedUser && (
            <div className="form-group">
              <label>كلمة المرور:</label>
              <input
                type="password"
                name="password"
                value={userForm.password}
                onChange={handleInputChange}
                className={`form-control ${formErrors.password ? "error" : ""}`}
              />
              {formErrors.password && (
                <span className="error-message">{formErrors.password}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label>الدور:</label>
            <select
              name="role"
              value={userForm.role}
              onChange={handleInputChange}
              className={`form-control ${formErrors.role ? "error" : ""}`}
            >
              <option value="">اختر الدور</option>
              {roles.map((role, index) => (
                <option key={index} value={role}>
                  {role === "member" ? "عضو" : role}
                </option>
              ))}
            </select>
            {formErrors.role && (
              <span className="error-message">{formErrors.role}</span>
            )}
          </div>

          {/* الحقول الإضافية للعضو */}
          {additionalFields.includes("city") && (
            <div className="form-group">
              <label>المدينة:</label>
              <select
                name="city"
                value={userForm.city}
                onChange={handleInputChange}
                className={`form-control ${formErrors.city ? "error" : ""}`}
              >
                <option value="">اختر المدينة</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              {formErrors.city && (
                <span className="error-message">{formErrors.city}</span>
              )}
            </div>
          )}

          {additionalFields.includes("district") && userForm.city && (
            <div className="form-group">
              <label>الحي:</label>
              <select
                name="district"
                value={userForm.district}
                onChange={handleInputChange}
                className={`form-control ${formErrors.district ? "error" : ""}`}
              >
                <option value="">اختر الحي</option>
                {districts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
              {formErrors.district && (
                <span className="error-message">{formErrors.district}</span>
              )}
            </div>
          )}

          {additionalFields.includes("phoneNumber") && (
            <div className="form-group">
              <label>رقم الهاتف:</label>
              <input
                type="tel"
                name="phoneNumber"
                value={userForm.phoneNumber}
                onChange={handleInputChange}
                className={`form-control ${
                  formErrors.phoneNumber ? "error" : ""
                }`}
                dir="ltr"
              />
              {formErrors.phoneNumber && (
                <span className="error-message">{formErrors.phoneNumber}</span>
              )}
            </div>
          )}

          {additionalFields.includes("birthDate") && (
            <div className="form-group">
              <label>تاريخ الميلاد:</label>
              <input
                type="date"
                name="birthDate"
                value={userForm.birthDate}
                onChange={handleInputChange}
                className="form-control"
                dir="ltr"
              />
            </div>
          )}

          {additionalFields.includes("points") && (
            <div className="form-group">
              <label>النقاط:</label>
              <input
                type="number"
                name="points"
                value={userForm.points}
                onChange={handleInputChange}
                className="form-control"
                min="0"
              />
            </div>
          )}

          {additionalFields.includes("membershipLevel") && (
            <div className="form-group">
              <label>مستوى العضوية:</label>
              <select
                name="membershipLevel"
                value={userForm.membershipLevel}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="bronze">برونزي</option>
                <option value="silver">فضي</option>
                <option value="gold">ذهبي</option>
                <option value="platinum">بلاتيني</option>
              </select>
            </div>
          )}

          <div className="form-buttons">
            <button type="submit" className="primary-button" disabled={loading}>
              {loading
                ? "جاري المعالجة..."
                : selectedUser
                ? "حفظ التعديلات"
                : "إضافة المستخدم"}
            </button>
            <button
              type="button"
              onClick={clearForm}
              className="secondary-button"
              disabled={loading}
            >
              إعادة تعيين
            </button>
          </div>
        </form>
      </div>

      {/* شريط البحث والفلترة */}
      <div className="search-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="بحث بالاسم"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            onClick={fetchUsers}
            className="search-button"
            disabled={loading}
          >
            بحث
          </button>
        </div>
        <select
          value={recordsPerPage}
          onChange={(e) => setRecordsPerPage(Number(e.target.value))}
          className="records-per-page"
          disabled={loading}
        >
          <option value={20}>20 سجل</option>
          <option value={50}>50 سجل</option>
          <option value={100}>100 سجل</option>
        </select>
      </div>

      {/* جدول المستخدمين */}
      <div className="table-container">
        {loading ? (
          <div className="loading">جاري التحميل...</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>البريد الإلكتروني</th>
                <th>الدور</th>
                <th>المدينة</th>
                <th>الحالة</th>
                <th>كلمة المرور</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name || ""}</td>
                  <td>{user.email || ""}</td>
                  <td>{user.role === "member" ? "عضو" : user.role || ""}</td>
                  <td>{user.city || "غير محدد"}</td>
                  <td>
                    <span
                      className={
                        user.isActive ? "status-active" : "status-inactive"
                      }
                    >
                      {user.isActive ? "نشط" : "معطل"}
                    </span>
                  </td>
                  <td>
                    {user.plainPassword ? (
                      <div className="password-cell">
                        <span className="password-text" title={user.plainPassword}>
                          {user.plainPassword}
                        </span>
                        <button
                          className={`copy-btn${copiedUserId === user.id ? " copied" : ""}`}
                          onClick={() => handleCopyPassword(user.id, user.plainPassword)}
                          title="نسخ كلمة المرور"
                        >
                          {copiedUserId === user.id ? "✓" : "📋"}
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: "#aaa", fontSize: "13px" }}>—</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="edit-button"
                      disabled={loading}
                      title="تعديل"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="delete-button"
                      disabled={loading}
                      title="حذف"
                    >
                      🗑️
                    </button>
                    <button
                      onClick={() => handleShowUserDetails(user)}
                      className="details-button"
                      disabled={loading}
                      title="المزيد"
                    >
                      👁️
                    </button>
                    <button
                      onClick={() => handleActivateOrRegenerate(user)}
                      className="activate-button"
                      disabled={loading}
                    >
                      {user.isActive ? "توليد رابط جديد" : "تفعيل"}
                    </button>
                    {user.isActive && (
                      <button
                        onClick={() => handleToggleUser(user.id, user.isActive)}
                        className="deactivate-button"
                        disabled={loading}
                        title="تعطيل"
                      >
                        ⏸️
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* النافذة المنبثقة لعرض التفاصيل */}
      {showUserDetailsModal && userDetails && (
        <UserDetailsModal
          user={userDetails}
          onClose={() => setShowUserDetailsModal(false)}
        />
      )}
    </div>
  );
}

export default UserManagementPage;